import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    InputAdornment,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AttachMoneyOutlined, ExpandMore as ExpandMoreIcon, PercentOutlined } from '@mui/icons-material';

import { CampaignWizardStepProps } from './CampaignWizard';
import { CampaignFee } from '../../../../types/Campaign';
import { useCampaignWizardContext } from '../../../../hooks/useCampaignWizard';
import AccessGuard from '../../../../components/AccessGuard';
import { USER_GROUP_ADMIN, USER_GROUP_AGENCY } from '../../../../types/User';
import { CDTextField } from '../../../../components/CDTextField';
import ApiService from '../../../../ApiService';
import { useUserContext } from '../../../../hooks/Contexts/UserContext';

const NUMERIC_FIELDS = new Set([
    'agencyFixedCPM',
    'agencyIncrementalCPM',
    'cognitionCPM',
    'agencyFee',
    'cdPlatformFee',
    'amazonPassthroughUpcharge',
    'cdManagedService',
    'otherFee',
]);

// Matches backend CampaignService.populateCampaignFee() logic
function populateCampaignFee(agencyFee: CampaignFee, mediaType: string): CampaignFee {
    const fee: CampaignFee = { ...agencyFee };
    switch (mediaType) {
        case 'Display':
            if (agencyFee.displayFixedCPM == null) {
                fee.agencyFee = agencyFee.displayAgencyFee ?? 0;
            } else {
                fee.agencyFixedCPM = agencyFee.displayFixedCPM;
                fee.agencyIncrementalCPM = agencyFee.displayIncrementalCPM;
            }
            break;
        case 'Video':
            if (agencyFee.videoFixedCPM == null) {
                fee.agencyFee = agencyFee.videoAgencyFee ?? 0;
            } else {
                fee.agencyFixedCPM = agencyFee.videoFixedCPM;
                fee.agencyIncrementalCPM = agencyFee.videoIncrementalCPM;
            }
            break;
        case 'Online Video':
            if (agencyFee.onlineVideoFixedCPM == null) {
                fee.agencyFee = agencyFee.onlineVideoAgencyFee ?? 0;
            } else {
                fee.agencyFixedCPM = agencyFee.onlineVideoFixedCPM;
                fee.agencyIncrementalCPM = agencyFee.onlineVideoIncrementalCPM;
            }
            break;
        case 'Streaming TV':
            if (agencyFee.streamingTvFixedCPM == null) {
                fee.agencyFee = agencyFee.streamingTvAgencyFee ?? 0;
            } else {
                fee.agencyFixedCPM = agencyFee.streamingTvFixedCPM;
                fee.agencyIncrementalCPM = agencyFee.streamingTvIncrementalCPM;
            }
            break;
        case 'Audio':
            if (agencyFee.audioFixedCPM == null) {
                fee.agencyFee = agencyFee.audioAgencyFee ?? 0;
            } else {
                // Matches backend behavior (CampaignService.java)
                fee.agencyFee = agencyFee.audioFixedCPM;
                fee.agencyIncrementalCPM = agencyFee.audioIncrementalCPM;
            }
            break;
    }

    return fee;
}

function buildFeeSummary(fee: CampaignFee): string {
    const parts: string[] = [];

    if (fee.agencyFixedCPM != null) {
        parts.push(`Agency Fees: $${fee.agencyFixedCPM} CPM`);
    } else {
        parts.push(`Agency Fees: ${fee.agencyFee || 0}%`);
    }

    const cdParts: string[] = [];
    if (fee.cdPlatformFee) cdParts.push(`$${fee.cdPlatformFee}`);
    if (fee.amazonPassthroughUpcharge) cdParts.push(`${fee.amazonPassthroughUpcharge}%`);
    if (fee.cdManagedService) cdParts.push(`${fee.cdManagedService}%`);
    if (cdParts.length > 0) {
        parts.push(`CD Fees: ${cdParts.join(' + ')}`);
    }

    return parts.join(' | ');
}

type CampaignSetupFeesCardProps = {} & CampaignWizardStepProps;

export default function CampaignSetupFeesCard(props: CampaignSetupFeesCardProps) {
    const { campaign, onChange } = props;
    const { isNewCampaign, isDraftCampaign, dealer } = useCampaignWizardContext();
    const { userContext } = useUserContext();
    const feesPopulatedForKey = useRef<string>('');
    const [feesVersion, setFeesVersion] = useState(0);

    useEffect(() => {
        const agencyId = dealer?.agencyId;
        const mediaType = campaign.mediaType;
        const key = `${agencyId}-${mediaType}`;

        if (!agencyId || !mediaType) return;
        if (!(isNewCampaign || isDraftCampaign)) return;
        if (feesPopulatedForKey.current === key) return;

        feesPopulatedForKey.current = key;

        ApiService.getAgency(agencyId).then((response) => {
            const agencyCampaignFee = response.data.campaignFee;
            if (agencyCampaignFee) {
                const mappedFee = populateCampaignFee(agencyCampaignFee, mediaType);
                onChange({ campaignFee: mappedFee });
                setFeesVersion((v) => v + 1);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dealer?.agencyId, campaign.mediaType]);

    const fee = campaign.campaignFee;

    function handleFieldChange(event: ChangeEvent<HTMLInputElement>) {
        const field = event.target.name;
        const value = event.target.value;

        if (NUMERIC_FIELDS.has(field)) {
            if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
                return;
            }
            const normalized = value === '' ? 0 : value.replace(/^0+(?=\d)/, '');
            onChange({ campaignFee: { ...fee, [field]: normalized } });
            return;
        }

        onChange({ campaignFee: { ...fee, [field]: value } });
    }

    function handleSwitchChange(field: string, checked: boolean) {
        onChange({ campaignFee: { ...fee, [field]: checked } });
    }

    return (
        <AccessGuard
            accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
            condition={(uc) => uc.isAdmin() || uc.isOpsAdmin()}
        >
            <Accordion id="campaign-wizard-setup-fees" key={feesVersion} variant="outlined" sx={{ borderRadius: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', mr: 2 }}>
                        <Typography variant="body1" fontWeight={500}>
                            Agency & Cognition Fees
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto !important' }}>
                            {buildFeeSummary(fee)}
                        </Typography>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={3}>
                        <Alert severity="info">
                            Changes made to Agency Fees or CD Fees after a campaign has active flights will not apply to
                            existing flights. Updates will only affect flights created in the future.
                        </Alert>

                        {/* Agency Fee Section */}
                        <div>
                            <Typography variant="body1" fontWeight={700} sx={{ mb: 2 }}>
                                Agency Fee
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                {fee.agencyFixedCPM != null ? (
                                    <>
                                        <Grid size={4}>
                                            <CDTextField
                                                name="agencyFixedCPM"
                                                label="Fixed CPM"
                                                value={fee.agencyFixedCPM}
                                                fullWidth
                                                onChange={handleFieldChange}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <AttachMoneyOutlined fontSize="small" color="primary" />
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={4}>
                                            <CDTextField
                                                name="agencyIncrementalCPM"
                                                label="Incremental CPM"
                                                value={fee.agencyIncrementalCPM ?? ''}
                                                fullWidth
                                                onChange={handleFieldChange}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <AttachMoneyOutlined fontSize="small" color="primary" />
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={4}>
                                            <CDTextField
                                                name="cognitionCPM"
                                                label="DSP CPM Cost"
                                                value={fee.cognitionCPM ?? ''}
                                                fullWidth
                                                onChange={handleFieldChange}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <AttachMoneyOutlined fontSize="small" color="primary" />
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </>
                                ) : (
                                    <Grid size={4}>
                                        <CDTextField
                                            name="agencyFee"
                                            label="Agency Fees"
                                            value={fee.agencyFee}
                                            fullWidth
                                            onChange={handleFieldChange}
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PercentOutlined fontSize="small" color="primary" />
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </div>

                        {/* Cognition Digital Fees Section */}
                        <div>
                            <Typography variant="body1" fontWeight={700} sx={{ mb: 2 }}>
                                Cognition Digital(CD) Fees
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                {/* CD Platform Fee */}
                                <Grid size={4}>
                                    <CDTextField
                                        name="cdPlatformFee"
                                        label="CD Platform Fee"
                                        value={fee.cdPlatformFee}
                                        fullWidth
                                        disabled={userContext.isOpsAdmin()}
                                        onChange={handleFieldChange}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AttachMoneyOutlined fontSize="small" color="primary" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid size={8}>
                                    <Stack direction="row" alignItems="center" justifyContent="flex-end">
                                        <Switch
                                            checked={fee.includeCdPlatformFee}
                                            disabled={userContext.isOpsAdmin()}
                                            onChange={(e) =>
                                                handleSwitchChange('includeCdPlatformFee', e.target.checked)
                                            }
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            Include in Spend / Budget
                                        </Typography>
                                    </Stack>
                                </Grid>

                                {/* Amazon Passthrough Upcharge */}
                                <Grid size={4}>
                                    <CDTextField
                                        name="amazonPassthroughUpcharge"
                                        label="Amazon Passthrough Upcharge"
                                        disabled={userContext.isOpsAdmin()}
                                        value={fee.amazonPassthroughUpcharge}
                                        fullWidth
                                        onChange={handleFieldChange}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PercentOutlined fontSize="small" color="primary" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid size={8}>
                                    <Stack direction="row" alignItems="center" justifyContent="flex-end">
                                        <Switch
                                            checked={fee.includeAmazonPassthroughUpcharge}
                                            disabled={userContext.isOpsAdmin()}
                                            onChange={(e) =>
                                                handleSwitchChange('includeAmazonPassthroughUpcharge', e.target.checked)
                                            }
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            Include in Spend / Budget
                                        </Typography>
                                    </Stack>
                                </Grid>

                                {/* CD Managed Service - no include toggle, always included in spend/budget */}
                                <Grid size={4}>
                                    <CDTextField
                                        name="cdManagedService"
                                        label="CD Managed Service"
                                        value={fee.cdManagedService}
                                        disabled={userContext.isOpsAdmin()}
                                        fullWidth
                                        onChange={handleFieldChange}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PercentOutlined fontSize="small" color="primary" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid size={8} />

                                {/* Other Fee */}
                                <Grid size={4}>
                                    <CDTextField
                                        name="otherFee"
                                        label="Other Fee"
                                        value={fee.otherFee}
                                        disabled={userContext.isOpsAdmin()}
                                        fullWidth
                                        onChange={handleFieldChange}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AttachMoneyOutlined fontSize="small" color="primary" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <CDTextField
                                        name="otherFeeLabel"
                                        label="Other Fee Label"
                                        disabled={userContext.isOpsAdmin()}
                                        value={fee.otherFeeLabel}
                                        fullWidth
                                        onChange={handleFieldChange}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Stack direction="row" alignItems="center" justifyContent="flex-end">
                                        <Switch
                                            checked={fee.includeOtherFee}
                                            disabled={userContext.isOpsAdmin()}
                                            onChange={(e) => handleSwitchChange('includeOtherFee', e.target.checked)}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            Include in Spend / Budget
                                        </Typography>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </div>
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </AccessGuard>
    );
}
